<script setup>
import { markRaw, reactive, computed, watch, nextTick, onMounted } from 'vue';
import { wrapperController } from '@/js/controllers/wrapperController';
import { responsiveController } from '@/js/controllers/responsiveController';
import ModalDefault from '@/js/vue/modal/components/ModalDefault.vue';
import { isString } from '@/js/utils/type';
import { useVmModalController } from '@/js/vue/modal/useVmModalController'
import { camelToKebab, pascalToCamel } from "@/js/utils/string";

// -------------------------------------------------------
// Constants
// -------------------------------------------------------
const SHOW_ANIMATION_DELAY_MS = 500;
const FULLPANEL_HIDE_DELAY_MS = 200;
const POPUP_SHOW_DELAY_MS = 300;

defineOptions({
    name: 'VmModal',
})

// -------------------------------------------------------
// Helpers
// -------------------------------------------------------
function getScrollTop() {
    return document.documentElement.scrollTop || document.body.scrollTop;
}

/**
 * 指定時間待機するPromise
 * @param {number} ms ミリ秒
 * @param {AbortSignal} [signal] キャンセル可能にするシグナル（オプション）
 * @returns {Promise<void>}
 */
function delay(ms, signal) {
    return new Promise((resolve, reject) => {
        const timerId = setTimeout(resolve, ms);
        // シグナルからのキャンセルリッスン
        if (signal) {
            signal.addEventListener('abort', () => {
                clearTimeout(timerId);
                reject(new DOMException('Aborted', 'AbortError'));
            }, {once: true});
        }
    });
}

/**
 * AbortController による中断エラーか判定する
 *
 * @param {unknown} error
 * @returns {boolean}
 */
function isAbortError(error) {
    return error?.name === 'AbortError';
}

// -------------------------------------------------------
// State
// -------------------------------------------------------
/**
 * コンポーネント関連の状態管理
 */
const componentState = reactive({
    /** 使用するローカルコンポーネント */
    component: null,
    /** componentネームを保持する */
    name: '',
    /** template内で使用する変数 */
    vars: {},
});

/**
 * アニメーション関連の状態管理
 */
const animationState = reactive({
    /**
     * モーダルが表示中、または表示アニメーション中か
     */
    isShow: false,
    /** 表示調整中か否か → transition の設定が一時的にnoneになる */
    isAdjusting: false,
    /** fullpanel の表示位置制御用座標
     * style.top に反映される */
    posY: 0,
    /** fullpanel 表示前のスクロール位置
     * close 時に復元するため保持する */
    tmpPosY: 0,
});

/**
 * モーダル設定・フロー管理
 */
const modalConfig = reactive({
    /** DOMにレンダリングするかどうかのフラグ */
    isActive: false,
    /** popup もしくは fullpanel */
    type: '',
    /**
     * type の自動切り替えを無効化するフラグ
     * true の場合、指定された type を維持する
     * false の場合、デバイスに応じて type を自動切り替えする
     */
    isFixedType: false,
    /** モーダルクローズするにはボタンクリックのみ */
    closeOnlyByBtns: false,
});

const modalState = reactive({
    /**
     * open() が呼ばれるたびに増加する世代番号。
     *
     * 主な用途:
     * - :key による同一コンポーネントの再マウント
     * - 表示アニメーション開始のトリガー
     * - 非同期処理の競合判定
     */
    openSeq: 0,
});

/**
 * 実行中アニメーションの AbortController
 *
 * open()/close() の競合時に、
 * 進行中のアニメーションを中断するために使用する。
 */
let animationAbortController = null;

/**
 * 最後に受け付けた open() の世代番号
 *
 * 非同期処理の完了時に、
 * 処理対象が最新の open() か判定するために使用する。
 */
let currentOpenSeq = 0;

/**
 * デバイス変更に伴う type 切り替え処理中フラグ
 *
 * responsiveController からイベントが連続発火した場合でも、
 * hide/show アニメーションが並走しないように制御する。
 */
let isChangingDevice = false;

const {
    open,
    confirm,
    close,
    fail,
    setBusy,
} = useVmModalController({
    present(modalOptions) {
        _open({
            component: modalOptions.component,
            vars: modalOptions.vars,
            type: modalOptions.type,
            close_only_by_btns: modalOptions.close_only_by_btns,
            name: modalOptions.name,
            methods: modalOptions.methods,
            tmpl: modalOptions.tmpl,
        })
    },
    dismiss(done) {
        // モーダルクローズ時のクリーンアップ
        // hideAnime()が完了後、done()を呼ぶことで
        // resetData()がコールされ、タイマーがクリアされる
        hideAnime(animationAbortController?.signal)
            .catch(error => {
                // キャンセルされた場合もdone()を呼び出す必要がある
                if (!isAbortError(error)) {
                    console.error('モーダルクローズ処理でエラー:', error);
                }
            })
            .finally(done)
    },
    reset() {
        resetData()
    },
})

// -------------------------------------------------------
// Computed
// -------------------------------------------------------
/**
 * $el に適用される classを算出
 * @returns {object}
 */
const class_obj = computed(() => ({
    'is-popup-show': modalConfig.type === 'popup' && animationState.isShow,
    'is-fullpanel-show': modalConfig.type === 'fullpanel' && animationState.isShow,
    'is-fullpanel-adjusting': modalConfig.type === 'fullpanel' && animationState.isAdjusting,
    [modalConfig.type]: true,
    [`${modalConfig.type}-${tmpl_name.value}`]: !!tmpl_name.value,
}));

/**
 * $el に適用される styleを算出
 * @returns {object}
 */
const style_obj = computed(() => {
    if (modalConfig.type === 'fullpanel') {
        return {
            top: `${animationState.posY}px`,
            'min-height': `${window.innerHeight}px`,
        };
    }
    return {};
});

/**
 * component がグローバルコンポーネントの場合、グローバルコンポーネントの名前を返す
 * component.template が x-template のID名で指定されている場合は テンプレートネームを返す
 * component.template がテンプレートベタ書きだった場合、別途nameを指定されていればそのnameを返す
 * 上記いずれにも当てはまらない場合は none を返す
 * @returns {string}
 */
const tmpl_name = computed(() => {
    if (!componentState.component) {
        return 'none';
    }
    if (isString(componentState.component)) {
        // グローバルコンポーネント
        return componentState.component;
    }

    if (componentState.component.name) {
        // Vueコンポーネント名（PascalCase）を、CSSクラス名として扱いやすいケバブケースへ変換したい
        const name = pascalToCamel(componentState.component.name)
        return camelToKebab(name);
    }

    if (componentState.component.template && isString(componentState.component.template)) {
        if (componentState.component.template.match(/^#/)) {
            return componentState.component.template.replace(/^#(tmpl-)?/, '');
        }
        if (componentState.name) {
            return componentState.name;
        }
    }
    return 'none';
});

// -------------------------------------------------------
// Private helpers
// -------------------------------------------------------
/**
 * 現在のデバイスに対する modalのtypeを返す
 * @param {string} [device] 未指定の場合はresponsiveControllerに現在のデバイスを問い合わせする
 * @returns {string} fullpanel か popup のいずれか
 */
function getTypeFromDevice(device) {
    const _device = device || responsiveController.getCurrentDevice();
    const device2type = {sp: 'fullpanel', pad: 'popup', pc: 'popup'};
    return device2type[_device] || 'popup';
}

/** 背景エリアスクロールをとめる (type が popup のときのみ） */
function stopScroll() {
    wrapperController.fixed();
}

/** 背景エリアスクロールストップの解除 (type が popup のときのみ） */
function restartScroll() {
    wrapperController.cancelFixed();
}

/**
 * 画面全体をラップする要素を取得する
 *
 * @returns {HTMLElement|null}
 */
function getWrapperElement() {
    return document.querySelector('.l-wrapper');
}

// -------------------------------------------------------
// Methods
// -------------------------------------------------------
/**
 * デバイス変更時のtype切り替え処理
 * @param {string} device
 */
async function onChangeDevice(device) {
    if (modalConfig.isFixedType) return; // type固定時はデバイス変更による切り替えを行わない
    if (!modalConfig.isActive) return;
    if (!animationState.isShow) return;
    if (isChangingDevice) return;

    const nextType = getTypeFromDevice(device);

    if (modalConfig.type === nextType) return;

    isChangingDevice = true;

    try {
        await hideAnime(animationAbortController?.signal);
        modalConfig.type = nextType;
        await showAnime(animationAbortController?.signal);
    } catch (error) {
        // キャンセルされた場合は無視
        if (!isAbortError(error)) {
            console.error('デバイス変更時のアニメーション処理でエラー:', error);
        }
    } finally {
        isChangingDevice = false;
    }
}

/**
 * fullpanel表示アニメーション
 * @param {AbortSignal} [signal] キャンセル用シグナル
 */
async function showFullpanelAnime(signal) {
    animationState.posY = getScrollTop();
    // CSS側のアニメーション時間を待つ
    await delay(SHOW_ANIMATION_DELAY_MS, signal);

    animationState.isAdjusting = true;
    const wrapper = getWrapperElement();
    if (wrapper) {
        wrapper.style.display = 'none';
    }
    animationState.posY = 0;
    scrollTo(0, 0);
}

/**
 * popup表示アニメーション
 * @param {AbortSignal} [signal] キャンセル用シグナル
 */
async function showPopupAnime(signal) {
    stopScroll();
    await delay(POPUP_SHOW_DELAY_MS, signal);
}

/**
 * modal表示アニメーション
 * @param {AbortSignal} [signal] キャンセル用シグナル
 */
async function showAnime(signal) {
    animationState.isShow = true;

    if (modalConfig.type === 'fullpanel') {
        await showFullpanelAnime(signal);
    } else if (modalConfig.type === 'popup') {
        await showPopupAnime(signal);
    }
}

/**
 * fullpanel非表示アニメーション
 * @param {AbortSignal} [signal] キャンセル用シグナル
 */
async function hideFullpanelAnime(signal) {
    const wrapper = getWrapperElement();
    if (wrapper) {
        wrapper.style.display = 'block';
    }
    animationState.posY = animationState.tmpPosY;
    scrollTo(0, animationState.tmpPosY);

    // 位置調整のDOM処理に続いて非表示アニメーションをいれると表示がちらつくので待つ
    await delay(FULLPANEL_HIDE_DELAY_MS, signal);

    animationState.isAdjusting = false;
    animationState.posY = getScrollTop() + window.innerHeight;

    // アニメーション期間を待つ
    await delay(SHOW_ANIMATION_DELAY_MS, signal);
    animationState.isShow = false;
}

/**
 * popup非表示アニメーション
 * @param {AbortSignal} [signal] キャンセル用シグナル
 */
async function hidePopupAnime(signal) {
    restartScroll();
    animationState.isShow = false;

    // popup側のアニメーション時間
    await delay(POPUP_SHOW_DELAY_MS, signal);
}

/**
 * modal非表示アニメーション
 * @param {AbortSignal} [signal] キャンセル用シグナル
 */
async function hideAnime(signal) {
    if (modalConfig.type === 'fullpanel') {
        await hideFullpanelAnime(signal);
    } else if (modalConfig.type === 'popup') {
        await hidePopupAnime(signal);
    }
}

/**
 * dataの値を初期値に戻す
 *
 * ⚠️ Note: VmModalはプラグイン経由で一度だけマウントされ、
 * その後はアンマウントされることなくグローバルに保持されます。
 * したがって、onUnmountedではなく、このresetData()内で
 * タイマーリソースをクリーンアップします。
 *
 * このクリーンアップは以下の場面で発火します：
 * モーダルのクローズ時（dismiss）
 */
function resetData() {
    // 実行中のアニメーションをキャンセル
    // これが実質的なタイマークリーンアップの主要ポイント
    if (animationAbortController) {
        animationAbortController.abort();
        animationAbortController = null;
    }

    componentState.component = null;
    componentState.name = '';
    componentState.vars = {};

    animationState.isShow = false;
    animationState.isAdjusting = false;
    animationState.posY = 0;
    animationState.tmpPosY = 0;

    modalConfig.isActive = false;
    modalConfig.type = '';
    modalConfig.isFixedType = false;
    modalConfig.closeOnlyByBtns = false;
}

/**
 * x-templateベースのコンポーネントを生成する
 * @param {string} tmpl x-templateのID名
 * @param {object} methods ユーザーが指定するメソッド
 * @returns {object} コンポーネントオブジェクト
 */
function createTmplComponent(tmpl, methods) {
    const modalTemplateMethods = {
        handleClose() {
            this.$emit('close')
        },
        handleCancel() {
            this.$emit('cancel')
        },
        handleOk(payload = null) {
            this.$emit('ok', payload)
        }
    }

    return {
        template: `#${tmpl}`,
        props: {
            type: String,
            vars: Object,
        },
        methods: {
            ...modalTemplateMethods,
            ...methods,
        },
        emits: ['ok', 'cancel', 'close'],
    };
}

/**
 * オブジェクトからコンポーネントと設定値を解決する
 * @param {object} obj
 * @returns {{ _component: object, _vars: object, _type: string, _close_only_by_btns: boolean, _name: string }}
 */
function resolveComponent(obj) {
    let _component = null;
    const _vars = obj.vars || {};
    const _type = obj.type || '';
    const _close_only_by_btns = obj.close_only_by_btns ?? false;
    const _name = obj.name || '';


    if (obj.tmpl) {
        // x-template（ページ側に記載している場合）をベースにしたコンポーネント（レガシー対応）
        _component = createTmplComponent(obj.tmpl, obj.methods || {});
    } else if (obj.component) {
        // 通常のコンポーネント
        _component = obj.component;
    } else {
        // obj.tmpl が指定されていない場合はデフォルトを使用
        _component = ModalDefault;

        if (!_vars.title || !_vars.lead) {
            console.error('デフォルトテンプレートを使用する場合はtitleとleadパラメータが必須です');
            return null;
        }
    }

    return {_component, _vars, _type, _close_only_by_btns, _name};
}

/**
 * モーダルの状態を更新する
 * @param {object} params
 * @param {object} params._component
 * @param {object} params._vars
 * @param {string} params._type
 * @param {boolean} params._close_only_by_btns
 * @param {string} params._name
 */
function setModalState({_component, _vars, _type, _close_only_by_btns, _name}) {
    componentState.vars = _vars;
    componentState.component = markRaw(_component);
    componentState.name = _name;

    modalConfig.isFixedType = _type !== '';
    modalConfig.type = _type || getTypeFromDevice();
    modalConfig.isActive = true;
    modalConfig.closeOnlyByBtns = _close_only_by_btns;
}

/**
 * モーダル表示前の状態初期化
 *
 * fullpanel モードでは現在のスクロール位置を保持し、
 * 初期表示位置を画面外へ移動する。
 *
 * @param {number} seq open() ごとに発行される世代番号
 */
function initializeDisplay(seq) {
    if (seq !== currentOpenSeq) return;

    // 新しいAbortControllerを作成（新しいアニメーション用）
    animationAbortController = new AbortController();

    // 新しくモーダルを開く場合
    if (modalConfig.type === 'fullpanel') {
        // 現在のスクロール位置をとっておく
        animationState.tmpPosY = getScrollTop();
        // 初期位置は画面外
        animationState.posY = getScrollTop() + window.innerHeight;
    }
}

/**
 * モーダルの表示アニメーションを開始する。
 *
 * 呼び出し元で nextTick() 完了後に実行されることを前提とする。
 *
 * open() により発行された世代番号を用いて、
 * 最新のモーダル表示要求に対してのみ
 * showAnime() を実行する。
 *
 * @param {number} seq open() ごとに発行される世代番号
 */
async function handleRenderReady(seq) {
    if (!modalConfig.isActive) return; // nextTick待機中に close された場合は中断
    if (seq !== currentOpenSeq) return; // 古い ready を破棄

    try {
        await showAnime(animationAbortController?.signal);
    } catch (error) {
        if (!isAbortError(error)) {
            console.error('showAnime開始時のエラー:', error);
        }
    } finally {
        setBusy(false);
    }
}

/**
 * modalをopenする
 * ＜objが 連想配列objectの場合＞
 * @param {object} obj
 * @param {string} [obj.tmpl] x-templateのID名（レガシー）
 * @param {object} [obj.component] コンポーネント（SFC推奨）
 * @param {string} [obj.name] component が x-templateでない場合に名前として適用される
 * @param {object} [obj.vars] template内で使用する変数
 * @param {string} [obj.type] 指定された場合、data.is_fixed_type が true になり data.type に適用される
 * @param {object} [obj.methods] x-template ベース時のメソッド登録（レガシー）
 * @param {boolean} [obj.close_only_by_btns] true を指定された場合、Popup の背景クリックで閉じない
 * ※ 優先順位は tmpl > component > ModalDefault
 * ※ ModalDefault を使用する場合は、varsにtitle と lead があること必須
 */
function _open(obj) {
    // open() 経由でのみ呼び出される内部メソッド

    setBusy(true);

    // 新しいモーダルが開かれる際に前のアニメーションが残っていたらクリア
    // → タイマーリソースが確実に解放される
    if (animationAbortController) {
        animationAbortController.abort();
        animationAbortController = null;
    }

    // モーダルごとの世代番号を発行する
    const seq = ++modalState.openSeq;
    currentOpenSeq = seq;

    // ステップ1: コンポーネントと設定値を解決
    const resolved = resolveComponent(obj);

    // ステップ2: コンポーネント指定のバリデーション
    if (!resolved) {
        fail();
        return;
    }

    // ステップ3: 状態更新
    setModalState(resolved);

    // ステップ4: 表示初期化
    // 表示初期化処理を開始
    // 完了待ちは不要
    initializeDisplay(seq);
}

/**
 * 背景クリックによるモーダルクローズ
 *
 * closeOnlyByBtns=true の場合は閉じない。
 */
function closeByCover() {
    if (modalConfig.closeOnlyByBtns) return;

    close({ action: 'close' });
}

// -------------------------------------------------------
// Lifecycle
// -------------------------------------------------------
onMounted(() => {
    /* responsiveController にブレイクポイント切り替え時に呼んでほしいイベントをセット */
    responsiveController.setEvents({
        fn: onChangeDevice,
    });
});

/**
 * モーダル表示開始のための監視処理。
 *
 * open() による新しい表示要求を契機に表示アニメーションを開始する。
 *
 * 以下の条件を満たしたタイミングで
 * handleRenderReady() を実行する。
 *
 * - nextTick() による DOM 反映が完了している
 * - openSeq の世代チェックを通過している
 *
 * これにより、同一コンポーネントを再表示する場合や、
 * 連続して open() が呼ばれた場合でも
 * 最新のモーダルに対してのみ表示処理を実行する。
 */
watch(
    () => modalState.openSeq,
    async () => {
        const seq = currentOpenSeq;

        await nextTick();

        // nextTick待機中に状態が変わったら中断
        if (seq !== currentOpenSeq) {
            return;
        }

        await handleRenderReady(seq);
    }
);

// -------------------------------------------------------
// Expose（window.VmModal から open / close 等を呼べるようにする）
// -------------------------------------------------------
defineExpose({
    open,
    close,
    confirm,
});
</script>

<template>
    <div v-if="modalConfig.isActive" :class="class_obj" :style="style_obj" @click.self="closeByCover">
    <component :key="modalState.openSeq"
               :is="componentState.component"
               :type="modalConfig.type"
               :vars="componentState.vars"
               @ok="(payload) => close({ action: 'ok', payload })"
               @cancel="() => close({ action: 'cancel', payload: null })"
               @close="() => close({ action: 'close', payload: null })"></component>
    </div>
</template>

